import React, { useState } from 'react';

interface MealHistoryReportProps {
  mealData: any;
}

interface Meal {
  id: number;
  description: string;
  foods: any[];
  totals: any;
  timestamp: string;
  meal_date: string;
  image_path?: string;
  context: string;
}

export const MealHistoryReport: React.FC<MealHistoryReportProps> = ({ mealData }) => {
  const [sortBy, setSortBy] = useState<'date' | 'calories' | 'name'>('date');
  const [filterBy, setFilterBy] = useState<string>('all');
  const [showImages, setShowImages] = useState(true);
  const [selectedNutrient, setSelectedNutrient] = useState<string>('calories');

  if (!mealData?.portfolio) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-gray-500">Loading meal history...</p>
        </div>
      </div>
    );
  }

  const meals: Meal[] = mealData.portfolio;
  
  // Filter meals
  const filteredMeals = meals.filter(meal => {
    if (filterBy === 'all') return true;
    return meal.context?.toLowerCase().includes(filterBy.toLowerCase());
  });

  // Sort meals
  const sortedMeals = [...filteredMeals].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      case 'calories':
        return (b.totals?.calories || 0) - (a.totals?.calories || 0);
      case 'name':
        return a.description.localeCompare(b.description);
      default:
        return 0;
    }
  });

  const formatDate = (timestamp: string, meal_date?: string) => {
    if (meal_date) {
      const date = new Date(meal_date);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const getContextIcon = (context: string) => {
    switch (context?.toLowerCase()) {
      case 'breakfast': return '🌅';
      case 'lunch': return '🌞';
      case 'dinner': return '🌙';
      case 'snack': return '🍎';
      default: return '🍽️';
    }
  };

  // Nutrient options for dropdown
  const nutrientOptions = [
    { value: 'calories', label: 'Calories', unit: 'cal' },
    { value: 'protein', label: 'Protein', unit: 'g' },
    { value: 'carbs', label: 'Carbohydrates', unit: 'g' },
    { value: 'fat', label: 'Fat', unit: 'g' },
    { value: 'fiber', label: 'Fibre', unit: 'g' },
    { value: 'sodium', label: 'Sodium', unit: 'mg' },
    { value: 'sugar', label: 'Sugar', unit: 'g' }
  ];

  const selectedNutrientInfo = nutrientOptions.find(n => n.value === selectedNutrient) || nutrientOptions[0];

  // Calculate stats based on FILTERED meals (not all meals)
  const totalStats = {
    totalMeals: filteredMeals.length,
    totalCalories: filteredMeals.reduce((sum, meal) => sum + (meal.totals?.calories || 0), 0),
    avgCaloriesPerMeal: filteredMeals.length > 0 ? Math.round(filteredMeals.reduce((sum, meal) => sum + (meal.totals?.calories || 0), 0) / filteredMeals.length) : 0,
    // Selected nutrient totals
    totalSelectedNutrient: filteredMeals.reduce((sum, meal) => sum + (meal.totals?.[selectedNutrient] || 0), 0),
    avgSelectedNutrient: filteredMeals.length > 0 ? Math.round(filteredMeals.reduce((sum, meal) => sum + (meal.totals?.[selectedNutrient] || 0), 0) / filteredMeals.length * 10) / 10 : 0,
    dateRange: filteredMeals.length > 0 ? {
      earliest: new Date(Math.min(...filteredMeals.map(m => new Date(m.timestamp).getTime()))),
      latest: new Date(Math.max(...filteredMeals.map(m => new Date(m.timestamp).getTime())))
    } : null
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Complete Meal History
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {totalStats.totalMeals} {filterBy !== 'all' ? filterBy : ''} meals • {totalStats.totalCalories.toLocaleString()} total calories
              {totalStats.dateRange && (
                <span className="ml-2">
                  • {formatDate(totalStats.dateRange.earliest.toISOString())} to {formatDate(totalStats.dateRange.latest.toISOString())}
                </span>
              )}
              {filterBy !== 'all' && (
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                  Filtered by {filterBy}
                </span>
              )}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 mt-4 sm:mt-0">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'calories' | 'name')}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value="date">Sort by Date</option>
              <option value="calories">Sort by Calories</option>
              <option value="name">Sort by Name</option>
            </select>
            
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Meals</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snacks</option>
            </select>

            <select
              value={selectedNutrient}
              onChange={(e) => setSelectedNutrient(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              {nutrientOptions.map(nutrient => (
                <option key={nutrient.value} value={nutrient.value}>
                  {nutrient.label}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowImages(!showImages)}
              className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-sm hover:bg-blue-100"
            >
              {showImages ? 'Hide Images' : 'Show Images'}
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* Summary Stats - Updates based on filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{totalStats.totalMeals}</div>
            <div className="text-sm text-gray-600">
              {filterBy === 'all' ? 'Total Meals' : `${filterBy.charAt(0).toUpperCase() + filterBy.slice(1)} Meals`}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{totalStats.totalCalories.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total Calories</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {selectedNutrient === 'calories' ? totalStats.totalCalories.toLocaleString() : Math.round(totalStats.totalSelectedNutrient * 10) / 10}
            </div>
            <div className="text-sm text-gray-600">
              Total {selectedNutrientInfo.label} ({selectedNutrientInfo.unit})
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {selectedNutrient === 'calories' ? totalStats.avgCaloriesPerMeal : totalStats.avgSelectedNutrient}
            </div>
            <div className="text-sm text-gray-600">
              Avg {selectedNutrientInfo.label}/Meal
            </div>
          </div>
        </div>

        {/* Meal List */}
        <div className="space-y-4">
          {sortedMeals.map((meal, index) => (
            <div
              key={meal.id || index}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                {/* Meal Image */}
                {showImages && meal.image_path && (
                  <div className="flex-shrink-0">
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}${meal.image_path}`}
                      alt={meal.description}
                      className="w-32 h-32 object-cover rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Meal Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        {getContextIcon(meal.context)}
                        {meal.description}
                      </h4>
                      <div className="text-sm text-gray-500 mt-1">
                        {formatDate(meal.timestamp, meal.meal_date)} • {formatTime(meal.timestamp)}
                        {meal.context && (
                          <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                            {meal.context}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {meal.totals?.calories || 0} cal
                      </div>
                      <div className="text-sm text-gray-500">
                        P: {meal.totals?.protein || 0}g • 
                        C: {meal.totals?.carbs || 0}g • 
                        F: {meal.totals?.fat || 0}g
                      </div>
                    </div>
                  </div>

                  {/* Foods in this meal */}
                  {meal.foods && meal.foods.length > 0 && (
                    <div className="mt-3">
                      <div className="text-sm text-gray-600 mb-2">Foods ({meal.foods.length}):</div>
                      <div className="flex flex-wrap gap-2">
                        {meal.foods.map((food, foodIndex) => (
                          <span
                            key={foodIndex}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {food.name}
                            {food.portion && (
                              <span className="ml-1 text-gray-500">({food.portion})</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredMeals.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No meals found matching your filters.
          </div>
        )}

        {filteredMeals.length !== meals.length && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            Showing {filteredMeals.length} of {meals.length} meals
          </div>
        )}
      </div>
    </div>
  );
};